import React, { useState, useEffect } from 'react';
import { Typography, Container, Grid, Box, CircularProgress, Button, Card } from '@mui/material';
import { Add as AddIcon, ListAlt as ListAltIcon } from '@mui/icons-material';
import ChecklistProgressCard from './components/ChecklistProgressCard';
import { db } from './firebase.ts';
import { collection, getDocs, query, where, onSnapshot } from 'firebase/firestore';
import AddChecklistPopup from './AddChecklistPopup'; // Import the new popup component
import { useTheme } from './ThemeContext'; // Import useTheme
import { isSameDay, startOfDay } from 'date-fns'; // Import date-fns functions
import { useTheme as useMuiTheme } from '@mui/material/styles'; // Import useMuiTheme
import useMediaQuery from '@mui/material/useMediaQuery'; // Import useMediaQuery
import { useNavigate } from 'react-router-dom';
import MobileHeader from './components/MobileHeader'; // Import MobileHeader

import { User } from 'firebase/auth';

interface Checklist {
  id: string;
  title: string;
  progress: number;
  totalTasks: number;
  completedTasks: number;
  type: string;
}

interface Task {
  id: string;
  completed: boolean;
  scheduledDate: Date | null;
  isRecurring: boolean;
  isRecurringInstance: boolean;
  recurrence: { type: string };
  nextOccurrenceDates: Date[];
}

interface HomeProps {
  user: User | null;
}

function Home({ user }: HomeProps) {
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const [checklists, setChecklists] = useState<Checklist[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isAddChecklistPopupOpen, setIsAddChecklistPopupOpen] = useState<boolean>(false);
  // Removed unreadCount state and its associated useEffect as mobile header elements are no longer in Home.tsx

  const { theme } = useTheme();
  const navigate = useNavigate();

  // Removed notification fetching logic as mobile header elements are no longer in Home.tsx
  // useEffect(() => {
  //   let unsubscribe: () => void;
  //   if (user) {
  //     const notificationsRef = collection(db, `users/${user.uid}/notifications`);
  //     const q = query(notificationsRef, where('read', '==', false));

  //     unsubscribe = onSnapshot(q, (snapshot) => {
  //       setUnreadCount(snapshot.size);
  //     }, (error: any) => {
  //       console.error("Error fetching unread notifications:", error);
  //     });
  //   } else {
  //     setUnreadCount(0);
  //   }

  //   return () => {
  //     if (unsubscribe) {
  //       unsubscribe();
  //     }
  //   };
  // }, [user]);

  useEffect(() => {
    const fetchChecklists = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const checklistsCollectionRef = collection(db, `users/${user.uid}/checklists`);
        const checklistSnapshot = await getDocs(checklistsCollectionRef);
          const checklistsData: Checklist[] = [];
        const taskPromises = [];

        for (const doc of checklistSnapshot.docs) {
          const checklist: { id: string; name: string } = { id: doc.id, name: doc.data().name as string }; // Assuming 'name' is a property
          
          // Fetch tasks for this checklist
          const tasksCollectionRef = collection(db, `users/${user.uid}/checklists/${checklist.id}/tasks`);
          const tasksSnapshot = await getDocs(tasksCollectionRef);
          const tasks: Task[] = tasksSnapshot.docs
            .map(taskDoc => {
              const data = taskDoc.data();
              return {
                id: taskDoc.id,
                completed: data.completed || false, // Ensure 'completed' exists
                scheduledDate: data.scheduledDate ? data.scheduledDate.toDate() : null,
                isRecurring: data.isRecurring || false,
                isRecurringInstance: data.isRecurringInstance || false,
                recurrence: data.recurrence || { type: 'none' },
                nextOccurrenceDates: Array.isArray(data.nextOccurrenceDates)
                  ? data.nextOccurrenceDates.map((ts: any) => ts.toDate())
                  : [],
              };
            });

          // Apply the same filtering logic as ChecklistsPage.js
          const today = new Date();
          today.setHours(0, 0, 0, 0); // Normalize to start of day for comparison

          const filteredTasks = tasks.filter((task: Task) => {
            const todayStart = startOfDay(today);

            if (!task.isRecurring) {
              return true;
            }

            if (task.isRecurring) {
              if (task.isRecurringInstance && task.scheduledDate) {
                return isSameDay(task.scheduledDate, todayStart);
              }
              if (!task.isRecurringInstance && Array.isArray(task.nextOccurrenceDates) && task.nextOccurrenceDates.length > 0) {
                return task.nextOccurrenceDates.some((occurrenceDate: Date) =>
                  isSameDay(occurrenceDate, todayStart)
                );
              }
            }
            return false;
          });

          const completedFilteredTasks = filteredTasks.filter((task: Task) => task.completed).length;
          const totalFilteredTasks = filteredTasks.length;
          const progress = totalFilteredTasks > 0 ? Math.round((completedFilteredTasks / totalFilteredTasks) * 100) : 0;

          checklistsData.push({
            id: checklist.id,
            title: checklist.name,
            progress: progress,
            totalTasks: totalFilteredTasks, // Now reflects filtered tasks
            completedTasks: completedFilteredTasks, // Now reflects filtered tasks
            type: 'today',
          });
        }
        setChecklists(checklistsData);
      } catch (err) {
        console.error("Error fetching checklists:", err);
        setError("Failed to load checklists.");
      } finally {
        setLoading(false);
      }
    };

    fetchChecklists();
  }, [user]);

  const handleAddChecklist = async (checklistName: string) => {
    // This function should ideally be passed down from App.js or handled via context
    // For now, a placeholder to avoid errors.
    console.log(`Adding checklist: ${checklistName}`);
    setIsAddChecklistPopupOpen(false);
    // In a real app, you'd add to Firestore and update state
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ textAlign: 'center', mt: 4 }}>
        <Typography color="error">{error}</Typography>
      </Container>
    );
  }

  const todayChecklists = checklists; // Display all checklists under "Today" for now
  const upcomingChecklists = []; // No specific upcoming logic yet

  return (
    <Container sx={{ pt: 0, px: 0 }} maxWidth={false}>
      {isMobile && <MobileHeader title="Today's Progression" />}

      {/* Main Content Area */}
      <Box sx={{ mt: isMobile ? '70px' : '30px', px: isMobile ? 2 : 3, pb: isMobile ? 2 : 3, backgroundColor: theme.colors.lightGrayBackground }}> {/* Adjusted mt and added light gray background */}
        {/* Today's Checklists */}
        <Box sx={{ mb: 4 }}>
          {isMobile ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}> {/* Removed gap and debug border */}
              {todayChecklists.length > 0 ? (
                todayChecklists.map((checklist) => (
                  <Box 
                    key={checklist.id} 
                    sx={{ 
                      flexBasis: '48%', /* Simplified flexBasis for two columns */
                      flexGrow: 0, /* Prevent growing */
                      flexShrink: 1, 
                      minWidth: 0, 
                      boxSizing: 'border-box', 
                      margin: '1%' /* Add margin for spacing */
                    }}
                  >
                    <ChecklistProgressCard
                      title={checklist.title}
                      progress={checklist.progress}
                    />
                  </Box>
                ))
              ) : (
                <Typography variant="body1" sx={{ mt: 2, color: theme.colors.lightText }}>
                  No checklists for today.
                </Typography>
              )}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}>
              {todayChecklists.length > 0 ? (
                todayChecklists.map((checklist) => (
                  <Box 
                    key={checklist.id} 
                    sx={{ 
                      flexBasis: '23%', /* For 4 columns on desktop */
                      flexGrow: 0, 
                      flexShrink: 1, 
                      minWidth: 0, 
                      boxSizing: 'border-box', 
                      margin: '1%' /* Add margin for spacing */
                    }}
                  >
                    <ChecklistProgressCard
                      title={checklist.title}
                      progress={checklist.progress}
                    />
                  </Box>
                ))
              ) : (
                <Typography variant="body1" sx={{ mt: 2, color: theme.colors.lightText }}>
                  No checklists for today.
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>

      <AddChecklistPopup
        open={isAddChecklistPopupOpen}
        onClose={() => setIsAddChecklistPopupOpen(false)}
        onAdd={handleAddChecklist}
      />
    </Container>
  );
}

export default Home;
